import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Group,
  GroupProps,
  Indicator,
  ModalProps,
  Stack,
} from "@mantine/core"
import { useTranslation } from "react-i18next"
import { IconFilterOff, IconFilter } from "@tabler/icons-react"
import { useBreakpoints } from "../../../utils/use-breakpoints"
import { DefaultModal } from "@components/default-modal"
import { keepPreviousData } from "@tanstack/react-query"

export type FilterValues = Record<string, unknown>

export function useResponsiveFilterQueryOptions(visible: boolean) {
  const { xs } = useBreakpoints()

  return {
    enabled: !xs || !visible,
    placeholderData: keepPreviousData,
  }
}

interface ResponsiveFilterGroupProps
  extends GroupProps, React.PropsWithChildren {
  visible: boolean
  setVisible: (value: boolean) => void
  filters?: FilterValues
  onReset?: () => void
  modalProps?: Omit<ModalProps, "opened" | "onClose">
}

export function ResponsiveFilterGroup({
  visible,
  setVisible,
  children,
  filters,
  onReset,
  modalProps,
  ...props
}: ResponsiveFilterGroupProps) {
  const { xs } = useBreakpoints()
  const { t } = useTranslation("app_server")
  const close = () => setVisible(false)
  const activeFilters = getActiveFilterCount(filters)
  return xs ? (
    <DefaultModal
      title={
        <Group
          w="100%"
          wrap="nowrap"
          style={{ gridTemplateColumns: "1fr auto 1fr", display: "grid" }}
        >
          <Box>
            <FilterResetButton filters={filters} onReset={onReset} />
          </Box>
          <Group gap="xs" wrap="nowrap">
            {t("Filter")}
            {activeFilters > 0 && (
              <Badge variant="filled">{activeFilters}</Badge>
            )}
          </Group>
        </Group>
      }
      opened={visible}
      onClose={close}
      zIndex={100}
      {...modalProps}
    >
      <Stack>{children}</Stack>
    </DefaultModal>
  ) : (
    <Group display={visible ? undefined : "none"} {...props}>
      {children}
      <FilterResetButton filters={filters} onReset={onReset} />
    </Group>
  )
}

interface FilterVisibilityButtonProps {
  filters?: FilterValues
  visible: boolean
  onChange: (value: boolean) => void
}

function hasFilterValue(value: unknown) {
  if (value === null || value === undefined || value === false) return false
  if (typeof value === "string") return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0

  return true
}

export function getActiveFilterCount(filters?: FilterValues) {
  return Object.values(filters ?? {}).filter(hasFilterValue).length
}

function FilterResetButton({
  filters,
  onReset,
}: Pick<ResponsiveFilterGroupProps, "filters" | "onReset">) {
  const { t } = useTranslation("app_server")

  if (!onReset || !getActiveFilterCount(filters)) return null

  return (
    <Button variant="subtle" onClick={onReset} aria-label={t("Reset")}>
      {t("Reset")}
    </Button>
  )
}

function FilterVisibilityButton({
  onChange,
  visible,
  filters,
}: FilterVisibilityButtonProps) {
  const activeFilters = getActiveFilterCount(filters)

  return visible ? (
    <ActionIcon variant="subtle" onClick={() => onChange(!visible)}>
      <IconFilterOff />
    </ActionIcon>
  ) : (
    <Indicator
      radius={"md"}
      zIndex={1}
      withBorder
      disabled={!activeFilters}
      label={activeFilters}
      size={20}
    >
      <ActionIcon
        variant={activeFilters ? "filled" : "subtle"}
        onClick={() => onChange(!visible)}
      >
        <IconFilter />
      </ActionIcon>
    </Indicator>
  )
}

ResponsiveFilterGroup.Button = FilterVisibilityButton
